<template>
  <div class="router-wrapper">
    <div class="element-wrapper">
      <simplert :useRadius="true" :useIcon="true" ref="simplert"></simplert>

      <div class="os-tabs-w">
        <div class="os-tabs-controls os-tabs-complex">
          <div class="nav-item">
            <h1>{{ $t("characteristics.characteristics") }}</h1>
          </div>
          <div class="nav-item nav-actions d-none d-sm-block">
            <router-link to="characteristics/create" class="button" color="primary" size="xlarge" icon="left">
              <i class="fas fa-plus"></i>
              <span>{{ $t("characteristics_index.add_new") }}</span>
            </router-link>
          </div>
        </div>
      </div>

      <div class="content-box">
        <div class="row">
          <div class="col-12">
            <span v-if="isSaving" class="float-right">{{ $t("general.saving") }}</span>
            <button type="button" class="button" v-if="didDrag && !isSaving" v-on:click="saveOrder">
              {{ $t("general.save_order") }}
            </button>
          </div>
        </div>
        <div class="table-responsive">
          <table class="table table-padded">
            <thead>
              <tr>
                <th scope="col">{{ $t("general.name") }}</th>
                <th scope="col">{{ $t("general.critical") }}</th>
                <th scope="col">{{ $t("general.unit") }}</th>
                <th scope="col">{{ $t("general.category") }}</th>
                <th scope="col">{{ $t("general.format") }}</th>
                <th scope="col">{{ $t("general.actions") }}</th>
              </tr>
            </thead>
            <draggable :list="characteristics" :element="'tbody'" @start="didDrag = true">
              <tr v-for="characteristic in characteristics" v-bind:key="characteristic.id">
                <td>
                  <span v-if="characteristic.shortcut">
                    <strong>{{ characteristic.shortcut }} -</strong>
                  </span>
                  {{ characteristic.localizedKey }}
                </td>
                <td><i class="fas fa-check" v-if="characteristic.isCritical" /></td>
                <td>{{ characteristic.unit.name }}</td>
                <td>{{ characteristic.category }}</td>
                <td>{{ $t(`data_types.${characteristic.dataType}`) }}</td>
                <td>
                  <span v-if="deletedCharacteristics.find(c => c.id == characteristic.id)" class="float-left">
                    <clip-loader :loading="true" :size="'20px'"></clip-loader>
                  </span>
                  <span v-else>
                    <i class="far fa-trash-alt link" v-on:click="showConfirmationDialog(characteristic)"></i>
                  </span>
                </td>
              </tr>
            </draggable>
          </table>
        </div>

        <div v-if="hasError || fetching || characteristics.length < 1" class="element-info-with-icon">
          <div class="element-info-icon">
            <div class="os-icon os-icon-grid-18 element-info-rotate"></div>
          </div>
          <div class="element-info-text">
            <h5 class="element-inner-header">
              {{ $t("characteristics.loading_characteristics") }}
            </h5>
            <div class="element-inner-desc">
              {{ $t("general.please_wait_data_from_cedoon") }}
            </div>
          </div>
        </div>

        <div v-if="hasError" class="element-info-with-icon">
          <div class="element-info-icon">
            <div class="os-icon os-icon-close"></div>
          </div>
          <div class="element-info-text">
            <h5 class="element-inner-header">{{ $t("general.error.oops") }}</h5>
            <div class="element-inner-desc">
              {{ $t("general.error.communication_error") }}
            </div>
          </div>
        </div>

        <div v-if="!fetching && characteristics.length < 1" class="element-info-with-icon">
          <div class="element-info-icon">
            <div class="os-icon os-icon-documents-03"></div>
          </div>
          <div class="element-info-text">
            <h5 class="element-inner-header">
              {{ $t("characteristics_index.no_characteristics_available") }}
            </h5>
            <div class="element-inner-desc">
              {{ $t("characteristics_index.start_by_adding_new_characteristics") }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import draggable from "vuedraggable";
import ClipLoader from "vue-spinner/src/ClipLoader.vue";

export default {
  name: "CharacteristicsIndex",
  data() {
    return {
      didDrag: false,
      isSaving: false,
      deletedCharacteristics: []
    };
  },
  components: {
    draggable,
    ClipLoader
  },
  methods: {
    ...mapActions({
      fetch: "characteristicsStore/fetch",
      deleteCharacteristics: "characteristicsStore/deleteCharacteristics",
      saveOrderAPI: "characteristicsStore/saveOrder"
    }),
    saveOrder() {
      this.characteristics.forEach((c, index) => (c.master_sort = index));
      this.isSaving = true;
      this.saveOrderAPI(this.characteristics).then(r => {
        this.didDrag = false;
        this.isSaving = false;
      });
    },
    deleteCharacteristic(characteristic) {
      this.deletedCharacteristics.push(characteristic);

      this.deleteCharacteristics(characteristic.id)
        .then(r => {
          if (r.data.deleteCharacterstic === "error") {
            const obj = {
              title: this.$i18n.t("general.error.error"),
              message: this.$i18n.t("characteristics.already_in_use"),
              type: "error"
            };
            this.$refs.simplert.openSimplert(obj);
            this.deletedCharacteristics = this.deletedCharacteristics.filter(c => c.id !== characteristic.id);
          } else {
            this.fetch();
          }
        })
        .catch(e => {
          this.deletedCharacteristics = this.deletedCharacteristics.filter(c => c.id !== characteristic.id);
          alert(e);
        });
    },
    showConfirmationDialog(characteristic) {
      const fn = this.deleteCharacteristic;
      const confirm = function() {
        fn(characteristic);
      };
      const obj = {
        title: this.$i18n.t("general.confirm"),
        message: this.$i18n.t("characteristics_index.dialog.message", {
          localizedKey: characteristic.localizedKey
        }),
        type: "warning",
        customCloseBtnText: this.$i18n.t("general.cancel"),
        customConfirmBtnText: this.$i18n.t("general.delete"),
        useConfirmBtn: true,
        customConfirmBtnClass: "button-red",
        onConfirm: confirm
      };
      this.$refs.simplert.openSimplert(obj);
    }
  },
  computed: {
    ...mapState("characteristicsStore", ["characteristics", "fetching", "hasError"])
  },
  mounted() {
    this.fetch();
  }
};
</script>

<style scoped></style>
